import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import { skipToken } from '@tanstack/react-query'
import {
  ActionIcon,
  Badge,
  Box,
  Collapse,
  Divider,
  Grid,
  Group,
  Loader,
  LoadingOverlay,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core'
import {
  Battery,
  ChevronDown,
  ChevronUp,
  CircleCheckBig,
  DiamondPlus,
  Droplets,
  GripVertical,
  Settings,
  TriangleAlert,
  Wifi,
  Zap,
} from 'lucide-react'
import {
  DndProvider,
  useDrag,
  useDrop,
} from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useOrderedList } from '@common/utils/hooks'
import { calculateDurationInDays } from '@common/utils/helperFunctions'
import {
  AlertGroup,
  AlertStatus,
} from '@data/alerts/model/alert.model'
import {
  Link,
  useNavigate,
} from 'react-router-dom'
import Device from '@data/device/model/device'
import { DeviceModalMantine } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/DeviceModal'
import { useDeviceById } from '@data/device/device'
import { useAuthStore } from '@store/auth.store'
import { getDurationColor } from '@components/main/Home/Dashboard/helpers'
import {
  useTicketById,
  useTicketsSummary,
} from '@data/tickets/tickets'
import { useDevicesLoading } from '@data/device/device'
import { useActiveAlertsQuery } from '@data/alerts/alerts'
import { TicketSummaryExtended } from '@components/main/LocationPerformance/LocationPerformance'
import { useAccountsQuery } from '@data/accounts/accounts'
import { useLocationsLoading } from '@data/locations/locations'
import { TicketDetails } from '@components/main/Tickets/TicketDetails'

// ---- TYPES ----

interface HomeStatsProps {
  healthyDevices: number
  ticketsSummary: TicketSummaryExtended[]
  alerts: AlertGroup[]
  newAddedDevices: Device[]
}

interface Item {
  id: number
  dUuid: string
  deviceId: string
  deviceName: string
  issueStartDate: string
  status?: string | number
  itemLink?: string
}

interface StatItem {
  id: string
  label: string
  total: number
  icon: React.ElementType
  color: string
  issueType: string
  items: Item[]
  nav?: { link: string; title: string }[]
  summary?: { key: string | number; description: string; value: number }[]
  clickable: boolean
  type: string
  loading: boolean
}

// ---- TICKET DETAILS LOADER ----

function TicketDetailsLoader({ dUuid, ticketId, onClose }: {
  dUuid: string
  ticketId: number
  onClose: () => void
}) {
  const { data: ticket, isFetching: isFetchingTicket, isPending: isPendingTicket } = useTicketById(dUuid, ticketId)

  if (isPendingTicket || isFetchingTicket) {
    return (
      <Modal opened onClose={onClose} centered size="xs" title="Loading Ticket">
        <Group
          justify="center"
          py="xl"
        >
          <Loader
            size="md"
            type="dots"
          />
        </Group>
      </Modal>
    )
  }

  if (!ticket) return null
  return <TicketDetails
    ticket={ticket}
    onClose={onClose}
  />
}

// ---- DRAG CONSTANT ----

const ITEM_TYPE = 'STAT_CARD'

// ---- CARD ----

const DraggableStatCard = React.memo(function DraggableStatCard({
                                                                  stat,
                                                                  index,
                                                                  isExpanded,
                                                                  moveCard,
                                                                  onToggle,
                                                                  onDeviceClick,
                                                                  onTicketClick,
                                                                }: {
  stat: StatItem
  index: number
  isExpanded: boolean
  moveCard: (dragIndex: number, hoverIndex: number) => void
  onToggle: (issueType: string) => void
  onDeviceClick: (dUuid: string) => void
  onTicketClick: (dUuid: string, ticketId: number) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { index },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    hover: (item: { index: number }, monitor) => {
      if (!ref.current) return

      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const rect = ref.current.getBoundingClientRect()
      const middleX = (rect.right - rect.left) / 2
      const clientX = monitor.getClientOffset()!.x - rect.left

      if (dragIndex < hoverIndex && clientX < middleX) return
      if (dragIndex > hoverIndex && clientX > middleX) return

      moveCard(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      style={{
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        zIndex: isExpanded ? 100 : 1,
      }}
    >
      <Paper
        shadow="sm"
        p="md"
        radius="md"
        withBorder
        onClick={() => stat.issueType !== 'no_issues' && onToggle(stat.issueType)}
        style={{
          cursor: 'pointer',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          transform: isExpanded ? 'scale(1.02)' : undefined,
          borderColor: isExpanded ? stat.color : undefined,
        }}
      >
        <Group justify="space-between" mb="xs">
          <Group gap="xs">
            <GripVertical size={16} color="#868e96" />

            {/* Icon with color background */}
            <div
              style={{
                padding: '0.4rem',
                borderRadius: '50%',
                backgroundColor: stat.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <stat.icon size={30} color="white" />
            </div>

            {/* Navigation links */}
            {stat.nav?.map((n, i) => (
              <Text
                key={i}
                component={Link}
                to={n.link}
                size="md"
                fw={500}
                ml="xs"
                onClick={(e) => e.stopPropagation()}
                style={{ textDecoration: 'none' }}
              >
                {n.title}
              </Text>
            ))}
          </Group>

          {stat.issueType !== 'health' && (
            isExpanded ? (
              <ChevronUp size={18} color={stat.color} />
            ) : (
              <ChevronDown size={18} color={stat.color} />
            )
          )}
        </Group>

        {/* Main content */}
        <Group justify="space-between" align="center">
          <Stack gap={0}>
            <Text size="md" c="dimmed">
              {stat.label}
            </Text>

            <Group gap="xs" align="center">
              <Text size="xl" fw={700} c={stat.color}>
                {stat.total}
              </Text>

              {/* Summary */}
              {stat.summary?.map((s, i) => (
                <Group key={i} gap={4}>
                  <Divider orientation="vertical" />
                  <Text size="xs" c="dimmed">
                    {s.description}
                  </Text>
                  <Text size="xs" fw={500}>
                    {s.value}
                  </Text>
                </Group>
              ))}
            </Group>
          </Stack>
        </Group>
      </Paper>

      {/* Collapse only when expanded */}

      <Collapse expanded={stat.issueType !== 'health' && isExpanded} keepMounted={false}>
        <Paper
          shadow="lg"
          p="md"
          radius="md"
          withBorder
          mt="xs"

          style={{
            borderLeft: `4px solid ${stat.color}`,
            zIndex: 1000,
            position: 'absolute',
            left: 0,
          }}
        >
          {stat.items.length === 0 ? (
            <Text size="sm" c="dimmed">
              No active items
            </Text>
          ) : (
            <Table.ScrollContainer maxHeight={400} minWidth={400}>
              <Table striped highlightOnHover stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Device ID</Table.Th>
                    <Table.Th>Device Name</Table.Th>
                    <Table.Th>Duration</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {stat.items.map(item => {
                    const days = calculateDurationInDays(item.issueStartDate)
                    const itemLink = item.itemLink
                    const isDevice = stat.type === 'device'
                    const isTicket = stat.type === 'ticket'

                    const rowOnClick = isDevice
                      ? () => onDeviceClick(item.dUuid)
                      : isTicket
                        ? () => onTicketClick(item.dUuid, item.id)
                        : itemLink
                          ? () => navigate(itemLink)
                          : undefined

                    return (
                      <Table.Tr
                        key={item.id}
                        onClick={rowOnClick}
                        style={{ cursor: rowOnClick ? 'pointer' : 'default' }}
                      >
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            {!isDevice && (
                              <Tooltip label="Device Configuration" withArrow>
                                <ActionIcon
                                  size="xs"
                                  variant="subtle"
                                  color="gray"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onDeviceClick(item.dUuid)
                                  }}
                                >
                                  <Settings size={13} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                            <Text size="sm" fw={500}>
                              {item.deviceId}
                            </Text>
                          </Group>
                        </Table.Td>

                        <Table.Td>
                          <Text size="sm">{item.deviceName}</Text>
                        </Table.Td>

                        <Table.Td>
                          <Badge
                            color={getDurationColor(days)}
                            variant="light"
                            size="sm"
                          >
                            {days} {days === 1 ? 'day' : 'days'}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Paper>
      </Collapse>
    </div>
  )
})

// ---- MAIN ----

export function HomeStats({
                            healthyDevices,
                            ticketsSummary,
                            alerts,
                            newAddedDevices,
                          }: HomeStatsProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [selectedDevice, setSelectedDevice] = useState<string>('')
  const [modalShow, setModalShow] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState<{ dUuid: string; id: number } | null>(null)
  const permissions = useAuthStore((state) => state.permissions)
  const { data: deviceData } = useDeviceById(
    selectedDevice,
    !!selectedDevice,
    permissions,
  )
  const { isFetching: isFetchingTickets } = useTicketsSummary(skipToken)
  const isLoadingDevices = useDevicesLoading()
  const { isLoading: isLoadingAlerts } = useActiveAlertsQuery()
  const { isLoading: isLoadingAccounts } = useAccountsQuery()
  const isLoadingLocations = useLocationsLoading()
  // const {data: ticketData, isLoading: isLoadingTicket, isFetching: isFetchingTicket, isPending: isPendingTicket} = useTicketById()

  const grouped = useMemo(() => {
    type Bucket = {
      items: { ts: number; i: Item }[]
      statusCount?: Record<string | number, number>
    }

    const buckets: Record<string, Bucket> = {
      no_flow: { items: [] },
      connectivity: { items: [] },
      battery: { items: [] },
      power: { items: [] },
      alerts: { items: [], statusCount: {} },
      newAddedDevices: { items: [] },
    }

    for (const t of ticketsSummary) {
      if (t.status !== 'open' && t.status !== 'in_progress' && t.status !== 'pending_review') continue
      const b = buckets[t.issueType]
      if (!b) continue

      b.items.push({
        ts: Date.parse(t.issueStartDate),
        i: {
          id: t.id,
          dUuid: t.dUuid,
          deviceId: t.deviceId,
          deviceName: t.deviceName,
          issueStartDate: t.issueStartDate,
        },
      })
    }

    for (const a of alerts) {
      if (a.status !== AlertStatus.OPEN && a.status !== AlertStatus.IN_PROGRESS) continue
      const b = buckets.alerts

      const item: Item = {
        id: a.alerts[0].id,
        dUuid: a.dUuid,
        deviceId: a.deviceId,
        deviceName: a.deviceLocation,
        issueStartDate: a.startAlertTimeUTC,
        status: a.status,
        itemLink: `/alert-queue/${a.gid}`,
      }
      b.items.push({ ts: Date.parse(a.startAlertTimeUTC), i: item })

      b.statusCount![a.status] =
        (b.statusCount![a.status] || 0) + 1
    }

    for (const d of newAddedDevices) {
      buckets.newAddedDevices.items.push({
        ts: Date.parse(d.deviceSettings.installDate),
        i: {
          id: d.dlId,
          dUuid: d.dUuid,
          deviceId: d.deviceId,
          deviceName: d.deviceName,
          issueStartDate: d.deviceSettings.installDate,
          status: 'added',
        },
      })
    }
    return Object.fromEntries(
      Object.entries(buckets).map(([k, v]) => [
        k,
        v.items.sort((a, b) => a.ts - b.ts).map(x => x.i),
      ]),
    ) as Record<string, Item[]>
  }, [ticketsSummary, alerts, newAddedDevices])


  const stats: StatItem[] = useMemo(() => {
    const ticketsLoading = isFetchingTickets || isLoadingDevices || isLoadingAccounts || isLoadingLocations

    const base = [
      {
        id: 'no_issues',
        label: `Healthy Location${healthyDevices === 1 ? '' : 's'}`,
        icon: CircleCheckBig,
        color: '#2f9e44',
        clickable: false,
        type: 'status',
        loading: ticketsLoading,
        total: healthyDevices,
        issueType: 'health',
        items: [] as Item[],
      },
      {
        id: 'no_flow',
        label: 'No Flow',
        icon: Droplets,
        color: '#1971c2',
        clickable: true,
        type: 'ticket',
        loading: ticketsLoading,
      },
      {
        id: 'connectivity',
        label: 'Offline',
        icon: Wifi,
        color: '#0ca678',
        clickable: true,
        type: 'ticket',
        loading: ticketsLoading,
      },
      {
        id: 'battery',
        label: 'Low Battery',
        icon: Battery,
        color: '#f59f00',
        clickable: true,
        type: 'ticket',
        loading: ticketsLoading,
      },
      {
        id: 'power',
        label: 'Powered Off',
        icon: Zap,
        color: '#e03131',
        clickable: true,
        type: 'ticket',
        loading: ticketsLoading,
      },
      {
        id: 'alerts',
        label: 'Active Water Alerts',
        icon: TriangleAlert,
        color: '#b83ef6',
        nav: [{ link: '/alert-queue', title: 'Review' }],
        clickable: true,
        type: 'alert',
        loading: isLoadingAlerts,
        // summary: [
        //   { key: 'OPEN', description: 'Open', value: 0 },
        //   { key: 'IN_PROGRESS', description: 'In Progress', value: 0 },
        // ],
      },
      {
        id: 'newAddedDevices',
        label: 'New Devices',
        icon: DiamondPlus,
        color: '#5431e0',
        clickable: true,
        type: 'device',
        loading: isLoadingDevices,
      },
    ]

    return base.map(cfg => {
      if (cfg.id === 'no_issues') return cfg as StatItem

      const items = grouped[cfg.id] ?? []

      return {
        ...cfg,
        issueType: cfg.id,
        total: items.length,
        items,
      }
    })
  }, [grouped, healthyDevices, isFetchingTickets, isLoadingAlerts, isLoadingDevices, isLoadingAccounts, isLoadingLocations])

  const { orderedItems, moveItem } = useOrderedList({
    items: stats,
    getId: s => s.id,
    storageKey: 'homeStatsOrder',
  })

  const toggle = useCallback((type: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(type) ? next.delete(type) : next.add(type)
      return next
    })
  }, [])

  const handleDeviceClick = useCallback((dUuid: string) => {
    setSelectedDevice(dUuid)
    setModalShow(true)
  }, [])

  const handleTicketClick = useCallback((dUuid: string, ticketId: number) => {
    setSelectedTicket({ dUuid, id: ticketId })
  }, [])

  function onHideDeviceModal() {
    setModalShow(false)
    setSelectedDevice('')
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Grid justify="center">
        {orderedItems.filter(stat => stat.id !== 'newAddedDevices' || stat.total > 0).map((stat, i) => (
          <Grid.Col
            span={{ base: 12, md: 6, lg: 4, xl: 3 }}
            key={`${stat.id}-grid`}
            mt="sm"
            align="center"
          >
            <Box pos="relative" key={stat.id}>
              <LoadingOverlay
                visible={stat.loading}
                zIndex={1000}
                overlayProps={{ radius: 'sm', blur: 2 }}
                loaderProps={{ color: 'blue', type: 'dots' }}
              />
              <DraggableStatCard
                key={stat.id}
                stat={stat}
                index={i}
                isExpanded={expanded.has(stat.issueType)}
                moveCard={moveItem}
                onToggle={toggle}
                onDeviceClick={handleDeviceClick}
                onTicketClick={handleTicketClick}
              />
            </Box>
          </Grid.Col>
        ))}
      </Grid>

      {selectedDevice && deviceData && (
        <DeviceModalMantine
          opened={modalShow}
          device={deviceData}
          onClose={onHideDeviceModal}
        />
      )}

      {selectedTicket && (
        <TicketDetailsLoader
          dUuid={selectedTicket.dUuid}
          ticketId={selectedTicket.id}
          onClose={() => setSelectedTicket(null)}
        />
      )}
    </DndProvider>
  )
}
